import {
  clone,
  concat,
  each,
  filter,
  find,
  first,
  includes,
  isArray,
  omit,
  sumBy,
} from 'lodash'
import api from '@/api/endpoints/filter'

import { DEFAULTFILTERS, FILTER_CACHE_VERSION, FILTER_CACHE_TTL_MS } from '@/constants'

// Shared, non-persisted promise used to dedupe concurrent fetches. If two
// components (or a component and the List route guard) ask for filters in the
// same tick while the cache is invalid, they share one in-flight request
// instead of firing GET /gardens/filters twice.
let filtersInFlight = null

export default {
  namespaced: true,

  state: {
    selectedFilters: [
      {
        filter: 'days_open',
        options: 'next-7-days',
      },
      {
        filter: 'opening_type',
        options: 'opening',
      },
    ],
    dynamicFilterSections: null,
    // When the cached sections were last fetched (epoch ms) and the cache
    // version they were fetched under. Persisted alongside dynamicFilterSections
    // so the TTL/version checks survive a reload. See ensureFilters.
    dynamicFilterSectionsCachedAt: null,
    dynamicFilterSectionsVersion: null,
  },

  mutations: {
    addBoundsFilter(state, data) {
      state.selectedFilters.push(data)
    },
    removeTag(state, data) {
      if (includes(['date_range'], data.type)) {
        state.selectedFilters = filter(state.selectedFilters, function (selectedFilter) {
          return !includes(['date_open_from', 'date_open_to'], selectedFilter.filter)
        })
        state.selectedFilters.push(find(DEFAULTFILTERS, ['filter', 'days_open']))
      } else {
        state.selectedFilters = filter(state.selectedFilters, function (selectedFilter) {
          return selectedFilter.filter !== data.filter
        })
      }
    },
    updateTag(state, data) {
      if (includes(['date_range'], data.type)) {
        state.selectedFilters = filter(state.selectedFilters, function (selectedFilter) {
          return selectedFilter.filter !== 'days_open'
        })
      }
      if (includes(['days_open'], data.filter)) {
        state.selectedFilters = filter(state.selectedFilters, function (selectedFilter) {
          return !includes(['date_open_from', 'date_open_to'], selectedFilter.filter)
        })
      }

      const existingFilter = find(state.selectedFilters, ['filter', data.filter])

      if (existingFilter) {
        let option = data.options
        let optionExists = null
        if (includes(['date_range'], data.type)) {
          existingFilter.options = data.options
          state.selectedFilters = filter(state.selectedFilters, function (selectedFilter) {
            return selectedFilter.filter !== 'days_open'
          })
        } else {
          if (isArray(existingFilter.options)) {
            option = first(data.options)
            optionExists = find(existingFilter.options, function (sectionOption) {
              return sectionOption === option
            })
          }
          if (!isArray(existingFilter.options)) {
            optionExists = existingFilter.options === option
          }
          if (optionExists && !includes(['radio', 'text', 'toggle'], data.type) && !data?.initial) {
            if (!includes(['multi_button', 'defaulted_toggle'], data.type)) {
              existingFilter.options = filter(existingFilter.options, function (sectionOption) {
                return sectionOption !== option
              })
            } else {
              existingFilter.options = null
            }
            if (!existingFilter.options || !existingFilter.options.length) {
              state.selectedFilters = filter(state.selectedFilters, function (selectedFilter) {
                return selectedFilter.filter !== data.filter
              })
            }
          } else {
            if (includes(['defaulted_toggle', 'radio', 'multi_button', 'text', 'toggle'], data.type)) {
              if (includes(['text'], data.type) && !data.options.length) {
                state.selectedFilters = filter(state.selectedFilters, function (selectedFilter) {
                  return selectedFilter.filter !== data.filter
                })
              }
              existingFilter.options = data.options
              if (includes(['toggle'], data.type)) {
                state.selectedFilters = filter(state.selectedFilters, function (selectedFilter) {
                  return selectedFilter.filter !== data.filter
                })
              }
            } else {
              existingFilter.options = concat(existingFilter.options, data.options)
            }
          }
        }
      } else {
        state.selectedFilters.push(omit(data, ['type', 'silent']))
      }
    },
    setDynamicFilterSections(state, sections) {
      state.dynamicFilterSections = sections
      state.dynamicFilterSectionsCachedAt = Date.now()
      state.dynamicFilterSectionsVersion = FILTER_CACHE_VERSION
    },
    resetFiltersWithoutSearch(state, data) {
      const defaultResetFilters = [
        {
          filter: 'days_open',
          options: 'next-7-days',
        },
        {
          filter: 'opening_type',
          options: 'opening',
        },
      ]
      const gardenFilter = find(state.selectedFilters, ['filter', 'garden'])
      if (gardenFilter) {
        defaultResetFilters.push(gardenFilter)
      }
      if (data.hasSelectedSearchResult || gardenFilter) {
        const geoBoundingBoxFilter = find(state.selectedFilters, ['filter', 'geo_bounding_box'])
        defaultResetFilters.push(geoBoundingBoxFilter)
      }
      state.selectedFilters = defaultResetFilters
    },
    resetFilters(state) {
      state.selectedFilters = [
        {
          filter: 'days_open',
          options: 'next-7-days',
        },
        {
          filter: 'opening_type',
          options: 'opening',
        },
      ]
    },
    clearFilters(state) {
      state.selectedFilters = [
        {
          filter: 'days_open',
          options: 'next-7-days',
        },
        {
          filter: 'opening_type',
          options: 'opening',
        },
      ]
    },
    clearState(state) {
      state.dynamicFilterSections = []
      state.selectedFilters = [
        {
          filter: 'days_open',
          options: 'next-7-days',
        },
        {
          filter: 'opening_type',
          options: 'opening',
        },
      ]
    },
  },

  actions: {
    clearFilters: ({ commit }) => new Promise((resolve) => {
      commit('clearFilters')
      resolve()
    }),
    clearState: ({ commit }) => new Promise((resolve) => {
      commit('clearState')
      resolve()
    }),
    fetchAllFiters: ({ commit }) => new Promise((resolve, reject) => {
      api.all((response) => {
        const responseData = response.data
        commit('setDynamicFilterSections', responseData)
        resolve(response)
      }, (error) => reject(error))
    }),
    // Fetch the dynamic filter sections only when the cache is missing, empty,
    // a different version, or older than the TTL. Otherwise resolve immediately
    // and serve the cached copy. A failed fetch leaves any existing sections
    // untouched (setDynamicFilterSections only commits on success), so an
    // offline TTL-expiry keeps showing the last-known filters rather than a
    // blank UI. Concurrent callers share one in-flight request.
    ensureFilters: ({ state, dispatch }) => {
      const sections = state.dynamicFilterSections
      const count = sections
        ? (isArray(sections) ? sections.length : Object.keys(sections).length)
        : 0
      const cachedAt = state.dynamicFilterSectionsCachedAt
      // Date.now() - cachedAt is negative if the device clock moved backwards
      // since caching (a future timestamp); treat that as invalid too.
      const age = cachedAt ? Date.now() - cachedAt : Infinity
      const fresh = count > 0
        && state.dynamicFilterSectionsVersion === FILTER_CACHE_VERSION
        && age >= 0
        && age < FILTER_CACHE_TTL_MS

      if (fresh) {
        return Promise.resolve()
      }

      if (!filtersInFlight) {
        filtersInFlight = dispatch('fetchAllFiters')
          .finally(() => { filtersInFlight = null })
      }
      return filtersInFlight
    },
    updateTag: ({ commit }, newFilter) => new Promise((resolve) => {
      if (includes(['date_range'], newFilter.type)) {
        each(newFilter.options, function (rangeDate, rangeKey) {
          commit('updateTag', {
            filter: rangeKey,
            type: 'date_range',
            options: rangeDate,
          })
        })
      } else {
        commit('updateTag', newFilter)
      }
      resolve()
    }),
    removeFilter: ({
      commit,
      dispatch,
      rootState,
      getters,
    }, filterToRemove) => new Promise((resolve) => {
      commit('removeTag', filterToRemove)

      const hasSelectedSearchResult = rootState.location.selectedLocation || getters.hasGardenFilter ? true : false
      dispatch('garden/filterGardens', { fromMap: hasSelectedSearchResult }, { root: true })
      resolve()
    }),
    addFilter: ({
      commit, state, dispatch, rootState, getters,
    }, newFilter) => new Promise((resolve) => {
      if (includes(['date_range'], newFilter.type)) {
        each(newFilter.options, function (rangeDate, rangeKey) {
          commit('updateTag', {
            filter: rangeKey,
            type: 'date_range',
            options: rangeDate,
          })
        })
      } else {
        // No open_status -> days_open coupling here on purpose: resetting the
        // visit date to "All Dates" is only wanted when a garden NAME is entered,
        // and that path sets it explicitly (applyGardenNameSwitch / searchGardens).
        // Toggling this filter in any other context must leave the date as-is.
        commit('updateTag', newFilter)
      }

      if (!newFilter.silent) {
        const hasSelectedSearchResult = rootState.location.selectedLocation || getters.hasGardenFilter ? true : false
        dispatch('garden/filterGardens', { fromMap: hasSelectedSearchResult }, { root: true })
      }

      resolve()
    }),
    resetFilters: ({ commit }) => new Promise((resolve) => {
      commit('resetFilters')
      resolve()
    }),
    resetFiltersAndGardens: ({ commit, dispatch, rootState }) => new Promise((resolve) => {
      const hasSelectedSearchResult = rootState.location.selectedLocation ? true : false

      commit('resetFiltersWithoutSearch', { hasSelectedSearchResult })
      dispatch('garden/filterGardens', [{}], { root: true })
      resolve()
    }),
    addBoundsFilter: ({ commit, dispatch }, bounds) => new Promise((resolve) => {
      const boundsFilter = {
        filter: 'geo_bounding_box',
        type: 'text',
        options: `${bounds.top_right.lat},${bounds.top_right.lng},${bounds.bottom_left.lat},${bounds.bottom_left.lng}`,
      }

      commit('updateTag', boundsFilter)
      dispatch('garden/filterGardens', { fromMap: true }, { root: true })
      resolve()
    }),
  },

  getters: {
    selectedFilters: (state) => state.selectedFilters,

    defaultFilters: () => DEFAULTFILTERS,

    dynamicFilterSections: (state) => state.dynamicFilterSections,

    defaultFiltersQuery: () => {
      const filters = {
      }
      each(DEFAULTFILTERS, function (defaultFilter) {
        const filterName = defaultFilter.filter === 'garden' ? 'garden_name' : defaultFilter.filter
        filters[`filter[${filterName}]`] = clone(defaultFilter.options)
      })
      return filters
    },

    selectedFiltersQuery: (state, getters) => {
      const filters = {}
      each(getters.selectedFilters, function (selectedFilter) {
        const filterName = selectedFilter.filter === 'garden' ? 'garden_name' : selectedFilter.filter
        filters[`filter[${filterName}]`] = selectedFilter.options
      })
      return filters
    },

    selectedFiltersQueryCheck: (state, getters) => {
      const filters = {}
      each(getters.selectedFilters, function (selectedFilter) {
        const filterName = selectedFilter.filter === 'garden' ? 'garden_name' : selectedFilter.filter
        filters[`filter[${filterName}]`] = (isArray(selectedFilter.options) && selectedFilter.options.length === 1) ? first(selectedFilter.options) : selectedFilter.options
      })
      return filters
    },
    hasGardenFilter: (state, getters) => {
      return getters.sectionFilterSelected('garden').length ? true : false
    },
    filterTypeByArray: (state) => (arrayOfLabels) => {
      return state.dynamicFilterSections ? filter(state.dynamicFilterSections, function (filterType, filterTypeIndex) {
        return includes(arrayOfLabels, filterTypeIndex)
      }) : []
    },

    dynamicFilterSectionByOpeningType: (state, getters) => {
      return getters.dynamicFilterSectionByKey('opening_type')
    },

    dynamicFilterSectionByKey: (state) => (key) => {
      return state.dynamicFilterSections ? find(state.dynamicFilterSections, function (dynamicFilterSection, dynamicFilterSectionIndex) {
        return dynamicFilterSectionIndex === key
      }) : null
    },

    dynamicFiltersOptionsByLabel: (state, getters) => (label) => {
      return getters.filterTypeBySlug(label) ? getters.dynamicFilterSectionByLabel(label).options : null
    },

    filtersBySlugAndKey: (state, getters) => (slug, key) => {
      return filter(getters.filtersBySlug(slug), ['filter_key', key])
    },
    filterSelected: (state) => (filterSection, option) => {
      const foundFilterSection = find(state.selectedFilters, ['filter', filterSection]) ?? null
      if (foundFilterSection) {
        if (isArray(foundFilterSection.options)) {
          return find(foundFilterSection.options, function (sectionOption) {
            return sectionOption === option
          }) ? true : false
        }
        if (!isArray(foundFilterSection.options)) {
          return foundFilterSection.options === option ? true : false
        }
      }
      return false
    },
    sectionFilterSelected: (state) => (filterSection) => {
      const foundFilterSection = find(state.selectedFilters, ['filter', filterSection]) ?? null
      if (foundFilterSection) {
        return foundFilterSection.options
      }
      return []
    },

    selectedFilterOptionsBytypeAndFilterIds: (state, getters) => (sectionType, filterIdToGet) => {
      const filterToFilter = find(getters.filterSectionBySectionType(sectionType), ['id', filterIdToGet])

      return filter(filterToFilter.options, function (option) {
        return option.selected
      }) ?? []
    },

    selectedFilterOptionsCount: (state) => {
      return sumBy(state.selectedFilters, function (filterSection) {
        if (isArray(filterSection.options)) {
          return filterSection.options.length
        }
        return 1
      })
    },
  },
}
